import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ApiFetch } from '../App';

export interface RelationalAnalysisData {
  probabilita_performance?: string;
  performance_percepita?: string;
  warnings: string[];
  norme: Record<string, string>;
  medie_chiave: Record<string, string>;
  voce_bedrock: string;
  relational?: Record<string, number | string>;
  surveyId: string;
  [key: string]: any;
}

interface RelationalAnalysisProps {
  analysis: RelationalAnalysisData | null;
  apiFetch: ApiFetch;
}

const NORM_KEY_MAP: Record<string, string> = {
  Conoscersi: 'report.norm_conoscersi',
  Discutere: 'report.norm_discutere',
  Connettersi: 'report.norm_connettersi',
};

const MEDIE_KEY_MAP: Record<string, string> = {
  Fondamentali: 'analysis.medie_fondamentali',
  'Capitale sociale': 'analysis.medie_capitale_sociale',
  Crescita: 'analysis.medie_crescita',
  Risultati: 'analysis.medie_risultati',
  Sostenibilità: 'analysis.medie_sostenibilita',
  Velocità: 'analysis.medie_velocita',
};

const RELATIONAL_KEY_MAP: Record<string, string> = {
  Activation: 'analysis.relational_activation',
  Reciprocity: 'analysis.relational_reciprocity',
  OrganizationalAwareness: 'analysis.relational_awareness',
  CollectiveSensemaking: 'analysis.relational_sensemaking',
  OrganizationalRegulation: 'analysis.relational_regulation',
  LearningAndPlasticity: 'analysis.relational_plasticity',
};

const WARNING_KEY_MAP: Record<string, string> = {
  Frustrazione: 'analysis.warning_frustrazione',
  'Capacità di problem solving': 'analysis.warning_problem_solving',
  Stanchezza: 'analysis.warning_stanchezza',
  'Eccessiva zona di comfort': 'analysis.warning_comfort',
  'Orientamento tattico': 'analysis.warning_tattico',
  'Difficoltà nella messa a terra': 'analysis.warning_messa_a_terra',
  Frenesia: 'analysis.warning_frenesia',
  'Eccessiva tranquillità': 'analysis.warning_tranquillita',
  'Scarso apprendimento': 'analysis.warning_apprendimento',
  'Scarsa capacità realizzativa': 'analysis.warning_realizzativa',
  'Rischi di turnover': 'analysis.warning_turnover',
  'Scarsa spinta': 'analysis.warning_spinta',
};

const COLOR_KEY_MAP: Record<string, string> = {
  rosso: 'analysis.color_rosso',
  giallo: 'analysis.color_giallo',
  verde: 'analysis.color_verde',
};

const AREA_KEY_MAP: Record<string, string> = {
  Fondamentali: 'analysis.medie_fondamentali',
  NormeIndividuo: 'report.area_norme_individuo',
  NormeGruppo: 'report.area_norme_gruppo',
  NormeOrganizzazione: 'report.area_norme_organizzazione',
  CapitaleSociale: 'analysis.medie_capitale_sociale',
};

const OUTCOME_KEY_MAP: Record<string, string> = {
  Crescita: 'analysis.medie_crescita',
  Risultati: 'analysis.medie_risultati',
  Sostenibilita: 'analysis.medie_sostenibilita',
  Velocita: 'analysis.medie_velocita',
  PerformancePercepita: 'analysis.performance_percepita',
};

export const RelationalAnalysis: React.FC<RelationalAnalysisProps> = ({ analysis, apiFetch }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const translateColor = (value: string): string =>
    value.replace(/^(rosso|giallo|verde)/, (c) => t(COLOR_KEY_MAP[c] ?? c));

  const translateNormKey = (key: string): string =>
    NORM_KEY_MAP[key] ? t(NORM_KEY_MAP[key]) : key;

  const translateMedieKey = (key: string): string =>
    MEDIE_KEY_MAP[key] ? t(MEDIE_KEY_MAP[key]) : key;

  const translateRelationalKey = (key: string): string =>
    RELATIONAL_KEY_MAP[key] ? t(RELATIONAL_KEY_MAP[key]) : key;

  const translateWarning = (w: string): string => (WARNING_KEY_MAP[w] ? t(WARNING_KEY_MAP[w]) : w);

  const translateAreaKey = (key: string): string =>
    AREA_KEY_MAP[key] ? t(AREA_KEY_MAP[key]) : key;

  const translateOutcomeKey = (key: string): string =>
    OUTCOME_KEY_MAP[key] ? t(OUTCOME_KEY_MAP[key]) : key;

  const formatRelationalValue = (v: number | string): string =>
    typeof v === 'number' ? v.toFixed(1) : v;

  const downloadPpt = async () => {
    setLoading(true);
    try {
      const response = await apiFetch({
        surveyId: analysis!.surveyId.toString(),
        operation: 'download_ppt',
      });
      if (response.ok) {
        const jsonResponse = await response.json();
        const a = document.createElement('a');
        a.href = jsonResponse['output'];
        a.download = '';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error('An error occurred:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setLoadingPdf(true);
    try {
      const response = await apiFetch({
        surveyId: analysis!.surveyId.toString(),
        operation: 'download_pdf',
      });
      if (response.ok) {
        const jsonResponse = await response.json();
        window.open(jsonResponse['output'], '_blank');
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error('An error occurred:', error);
    } finally {
      setLoadingPdf(false);
    }
  };

  if (!analysis) return null;

  const relationalDims = analysis['relational']
    ? Object.entries(analysis['relational']).filter(([k]) => k !== '_raw_1to5')
    : [];

  return (
    <Box paddingX={4} style={{ textAlign: 'left' }}>
      <Typography variant="body2" paddingBottom={2}>
        <b>{t('analysis.probabilita_performance')}</b>:{' '}
        {translateColor(analysis['probabilita_performance'] ?? '')}
      </Typography>
      <Typography variant="body2" paddingBottom={2}>
        <b>{t('analysis.performance_percepita')}</b>:{' '}
        {translateColor(analysis['performance_percepita'] ?? '')}
      </Typography>
      {analysis['warnings'].length > 0 && (
        <Typography variant="body2" paddingBottom={2}>
          <b>{t('analysis.warnings')}</b>: {analysis['warnings'].map(translateWarning).join(', ')}
        </Typography>
      )}
      <Grid
        spacing={6}
        paddingBottom={2}
        container
        direction="row"
        sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}
      >
        <Grid>
          <Typography variant="body2">
            <b>{t('analysis.norme')}</b>
          </Typography>
          {Object.keys(analysis['norme']).map((n) => (
            <Typography variant="body2" key={n}>
              {translateNormKey(n)}: {translateColor(analysis['norme'][n])}
            </Typography>
          ))}
        </Grid>
        <Grid>
          <Typography variant="body2">
            <b>{t('analysis.medie')}</b>
          </Typography>
          {Object.keys(analysis['medie_chiave']).map((n) => (
            <Typography variant="body2" key={n}>
              {translateMedieKey(n)}: {analysis['medie_chiave'][n]}
            </Typography>
          ))}
        </Grid>
        {relationalDims.length > 0 && (
          <Grid>
            <Typography variant="body2">
              <b>{t('analysis.relational_dims')}</b>
            </Typography>
            {relationalDims.map(([k, v]) => (
              <Typography variant="body2" key={k}>
                {translateRelationalKey(k)}: {formatRelationalValue(v as number | string)}
              </Typography>
            ))}
          </Grid>
        )}
        {analysis['areaClassifications'] && (
          <Grid>
            <Typography variant="body2">
              <b>{t('report.area_classifications_title')}</b>
            </Typography>
            {Object.entries(
              analysis['areaClassifications'] as Record<string, { mean: number; color: string }>
            ).map(([key, block]) => (
              <Typography variant="body2" key={key}>
                {translateAreaKey(key)}:{' '}
                {translateColor(`${block.color} (${block.mean.toFixed(2)})`)}
              </Typography>
            ))}
          </Grid>
        )}
      </Grid>
      <Typography variant="body2">
        <b>{t('analysis.temi')}</b>
      </Typography>
      {analysis['voce_bedrock']
        .replaceAll('THEME: ', 'TEMA:')
        .replaceAll('|', ', ')
        .split('TEMA:')
        .filter((t) => t !== '')
        .map((segment, i) => (
          <Typography variant="body2" paddingBottom={2} key={i}>
            {segment}
          </Typography>
        ))}
      {analysis['performanceRecommendations'] && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <b>{t('report.recommendations_title')}</b>
          </Typography>
          {Object.entries(
            analysis['performanceRecommendations'] as Record<
              string,
              { outcome: string; rankedAreas: Array<{ area: string }> }
            >
          ).map(([key, rec]) => (
            <Typography variant="body2" key={key}>
              {translateOutcomeKey(key)}:{' '}
              {rec.rankedAreas[0] ? translateAreaKey(rec.rankedAreas[0].area) : '—'}
            </Typography>
          ))}
        </Box>
      )}
      <Button loading={loading} variant="contained" sx={{ mt: 3 }} onClick={downloadPpt}>
        {t('analysis.download_ppt')}
      </Button>
      <Button loading={loadingPdf} variant="contained" sx={{ mt: 3, ml: 2 }} onClick={downloadPdf}>
        {t('analysis.download_pdf')}
      </Button>
      <Button
        variant="outlined"
        sx={{ mt: 3, ml: 2 }}
        onClick={() => window.open(`/relational-report/${analysis!.surveyId}`, '_blank')}
      >
        {t('analysis.see_report')}
      </Button>
    </Box>
  );
};
